import type { ExitStrategyProps } from './types';
import React, { type FC, type LegacyRef } from 'react';
import {
  Badge,
  Button,
  Flex,
  Heading,
  Icon,
  List,
  ListItem,
  LogoBox,
  Section,
  Text,
  Timeline,
  TimelineEvent,
  TimelineEventDescription,
} from '@highereducation/hen-ui';
import { EventingLocation } from '../../types/enums';
import { useDynamicExperienceContext } from '../../providers/DynamicExperienceProvider';
import useFlowTrackingEvents from '../../hooks/useFlowTrackingEvents';
import useViewEvents from '../../hooks/useViewEvents';
import { createElementViewedPayload } from '../../utils/createEventingPayloadsDE';
import { createDslElementEventContext } from '../../utils/createPrqEventContext';
import { StudentLoansInlinePlacement } from './StudentLoansInlinePlacement';
import { interpolateCtaUrl } from '../../utils/interpolateCtaUrl';

const SCHOOL_LOGO_BASE_URL =
  'https://simple-storage-server.highereducation.com';

// Generic fallback for the extra CTA's `webElement.elementType`/`name` when
// no DSL/org `eventName` is configured. Distinct from Apply's own literal
// (`dexp-application-cta`) so the two CTAs never share an identity;
// client-specific values always come from `extraCta.eventName`, not a
// hardcoded string here.
const DEFAULT_EXTRA_CTA_ELEMENT_NAME = 'dexp-hosted-lead-application-cta';

const ExitStrategy: FC<ExitStrategyProps> = ({
  firstName,
  schoolName,
  schoolSlug,
  city,
  state,
  applicationCtaUrl,
  applicationCtaLabel,
  applicationCtaFieldMap,
  onCloseExperience,
  productClickedCorrelationId,
  dslConfigId,
  studentLoansInlineContent,
  extraCta,
  lead,
  partnerIdentifiers,
  showCloseButton,
}) => {
  const schoolLogoUrl = schoolSlug
    ? `${SCHOOL_LOGO_BASE_URL}/${schoolSlug}.png?w=130&h=80`
    : undefined;

  const { eventContext } = useDynamicExperienceContext();
  const { triggerElementClickedEvent } = useFlowTrackingEvents();
  const shouldShowStudentLoansInline = Boolean(studentLoansInlineContent);
  const getExtraCtaHref = () => {
    if (extraCta?.enabled !== true || !extraCta.destination?.url) {
      return undefined;
    }
    const result = interpolateCtaUrl({
      baseUrl: extraCta.destination.url,
      staticParams: extraCta.destination.staticParams,
      fieldMap: extraCta.destination.fieldMap,
      missingValue: extraCta.destination.missingValue,
      sources: { lead, partnerIdentifiers },
    });
    return 'href' in result ? result.href : undefined;
  };
  const extraCtaHref = getExtraCtaHref();
  const extraCtaLabel =
    extraCta?.label ?? extraCta?.destination?.label ?? 'Learn more';

  // The application/exit CTA element events (spec §7.4.1) fire on the DSL path
  // only (presence of `dslConfigId`) and only when the CTA is rendered. The
  // imperative result page passes no `dslConfigId`, so it emits nothing and its
  // payloads stay byte-identical (AC #3).
  const shouldTrackCta = Boolean(dslConfigId) && Boolean(applicationCtaUrl);

  // Application/exit CTA `webElement` identity, built from the same
  // `createDslElementEventContext` helper as the PRQ element events so the
  // governed DSL element wiring stays consistent across surfaces.
  const ctaEventContext = createDslElementEventContext({
    eventContext,
    productClickedCorrelationId,
    elementType: 'dexp-application-cta',
    name: 'dexp-application-cta',
    location: EventingLocation.THANK_YOU_PAGE,
    text: applicationCtaLabel,
    dslConfigId,
    outboundUrl: applicationCtaUrl,
    fieldMap: applicationCtaFieldMap,
  });

  // Extra CTA element events fire on the same DSL-gated basis as the Apply
  // CTA above, reusing the identical `createDslElementEventContext` helper
  // so both CTAs stay consistent. `name` comes from DSL/org config
  // (`extraCta.eventName`) — never a hardcoded client-specific string.
  const shouldTrackExtraCta = Boolean(dslConfigId) && Boolean(extraCtaHref);
  const extraCtaEventName =
    extraCta?.eventName?.trim() || DEFAULT_EXTRA_CTA_ELEMENT_NAME;
  const extraCtaEventContext = createDslElementEventContext({
    eventContext,
    productClickedCorrelationId,
    elementType: DEFAULT_EXTRA_CTA_ELEMENT_NAME,
    name: extraCtaEventName,
    location: EventingLocation.THANK_YOU_PAGE,
    text: extraCtaLabel,
    dslConfigId,
    outboundUrl: extraCtaHref,
    fieldMap: extraCta?.destination?.fieldMap,
  });

  // One `ElementViewed` per visible CTA. The desktop card and mobile footer are
  // mutually exclusive at runtime (Tailwind `hidden lg:flex` vs `lg:hidden`), so
  // only the breakpoint-visible CTA intersects and fires — guarding the
  // single-view requirement via CSS scoping. Apply and the extra CTA share
  // one container ref per breakpoint, so both events are queued together and
  // fire together when that container becomes visible.
  const elementViewedEvents = [
    ...(shouldTrackCta ? [createElementViewedPayload(ctaEventContext)] : []),
    ...(shouldTrackExtraCta
      ? [createElementViewedPayload(extraCtaEventContext)]
      : []),
  ];
  const { ref: desktopCtaRef } = useViewEvents(elementViewedEvents);
  const { ref: mobileCtaRef } = useViewEvents(elementViewedEvents);

  const handleApplication = (
    e: React.MouseEvent<HTMLButtonElement>,
    url: string,
  ) => {
    e.preventDefault();
    if (shouldTrackCta) {
      triggerElementClickedEvent(ctaEventContext);
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleExtraCta = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const href = getExtraCtaHref();
    if (!href) return;
    if (shouldTrackExtraCta) {
      triggerElementClickedEvent(extraCtaEventContext);
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <Section
      variant='light'
      padding='none'
      gap={null}
      className='relative h-full max-h-dvh flex flex-col overflow-hidden'
    >
      {showCloseButton && onCloseExperience && (
        <Button
          type='button'
          variant='iconOnly'
          size='sm'
          iconBefore='x'
          aria-label='Close'
          className='absolute right-4 top-4 z-20'
          onClick={onCloseExperience}
        />
      )}

      {/* School header */}
      <Flex
        direction='row'
        align='center'
        gap='lg'
        className='px-6 py-5 bg-white shrink-0 z-10'
      >
        {schoolLogoUrl && (
          <LogoBox src={schoolLogoUrl} alt={schoolName ?? ''} />
        )}
        <Flex direction='column' gap='xs' className='min-w-0 flex-1'>
          <Heading variant='h5' className='truncate'>
            {schoolName}
          </Heading>
          {city && state && (
            <Text variant='b4'>
              {city}, {state}
            </Text>
          )}
        </Flex>
      </Flex>

      {/* Scrollable content — sits between the pinned header and footer */}
      <Flex
        direction='column'
        gap='lg'
        className='px-10 py-10 flex-1 overflow-y-auto min-h-0'
      >
        <Flex direction='column' gap='xs'>
          <Heading variant='h2'>
            Congratulations{firstName ? `, ${firstName}` : ''}!
          </Heading>
          <Text variant='b4' color='subtext'>
            Your information was submitted successfully.
          </Text>
        </Flex>

        <Badge variant='light' size='lg'>
          Now Accepting Students
        </Badge>

        {(applicationCtaUrl || extraCtaHref) && (
          <Flex
            ref={desktopCtaRef as LegacyRef<HTMLDivElement>}
            direction='column'
            gap='md'
            className='hidden lg:flex bg-white px-8 py-7 rounded-md'
          >
            <Flex direction='column' gap='xs'>
              <Heading variant='h4'>
                {applicationCtaUrl ? 'Apply Online Today' : 'Next Steps'}
              </Heading>
              {applicationCtaUrl && (
                <Text variant='b4' color='subtext'>
                  Online applications are fast, convenient, and let you track
                  your acceptance status. Start yours today!
                </Text>
              )}
            </Flex>
            {applicationCtaUrl && (
              <Button
                variant='primaryFill'
                size='lg'
                className='w-full !h-auto min-h-10'
                onClick={(e) => handleApplication(e, applicationCtaUrl)}
              >
                {applicationCtaLabel || 'Start Your Application'}
              </Button>
            )}
            {extraCtaHref && (
              <Button
                variant='primaryFill'
                size='lg'
                className='w-full !h-auto min-h-10'
                onClick={handleExtraCta}
              >
                {extraCtaLabel}
              </Button>
            )}
          </Flex>
        )}

        {shouldShowStudentLoansInline && (
          <StudentLoansInlinePlacement
            content={studentLoansInlineContent}
            configId={dslConfigId}
          />
        )}

        <Timeline>
          <TimelineEvent
            title='What to expect from your application:'
            index={0}
            hideMarker
          >
            <TimelineEventDescription
              index={0}
              isLast={false}
              lineColor='primary'
              lineWidth='thick'
            >
              <List bulletStyle='bullet'>
                <ListItem>
                  Most applications don&apos;t take long to complete.
                </ListItem>
                <ListItem>
                  Be prepared to highlight your strengths, achievements, and
                  individuality to show the school who you are.
                </ListItem>
                <ListItem>
                  Depending on the school and degree level, you may be asked to
                  provide your GPA and test scores.
                </ListItem>
                <ListItem>
                  After submitting your application, track its status online.
                  The school should have a decision soon.
                </ListItem>
              </List>
            </TimelineEventDescription>
          </TimelineEvent>

          <TimelineEvent
            title='Preparing for a chat with your advisor:'
            index={1}
            hideMarker
          >
            <TimelineEventDescription
              index={1}
              isLast={false}
              lineColor='primary'
              lineWidth='thick'
            >
              <List bulletStyle='bullet'>
                <ListItem>
                  Generally, you can expect to be called immediately or within 2
                  business days, depending on the availability of the
                  school&apos;s counselors. If you miss your call, {schoolName}{' '}
                  will try again.
                </ListItem>
                <ListItem>
                  Calls average 20-30 minutes, and you&apos;ll typically receive
                  a follow-up email afterwards.
                </ListItem>
                <ListItem>
                  Be prepared to talk about your work and school history.
                </ListItem>
                <ListItem>Write down a few questions before the call.</ListItem>
              </List>
            </TimelineEventDescription>
          </TimelineEvent>

          <TimelineEvent
            title="Here's what other students are asking:"
            index={2}
            hideMarker
          >
            <TimelineEventDescription
              index={2}
              isLast={false}
              lineColor='primary'
              lineWidth='thick'
            >
              <List bulletStyle='bullet'>
                <ListItem>
                  What type of courses will I be taking for my degree?
                </ListItem>
                <ListItem>
                  What scholarships and financial aid are available?
                </ListItem>
                <ListItem>
                  How can I estimate my tuition cost, including books and fees?
                </ListItem>
              </List>
            </TimelineEventDescription>
          </TimelineEvent>
        </Timeline>
      </Flex>

      {/* Mobile footer — pinned below the scroll area, always visible */}
      {(applicationCtaUrl || extraCtaHref) && (
        <Flex
          ref={mobileCtaRef as LegacyRef<HTMLDivElement>}
          direction='column'
          gap='md'
          className='p-6 bg-white shrink-0 border-t border-gray-100 lg:hidden'
        >
          <Flex direction='column' gap='xs'>
            <Heading variant='h5'>{schoolName}</Heading>
            {city && state && (
              <Flex direction='row' align='center' gap='xs'>
                <Icon icon='map-pin' size='sm' color='secondary' aria-hidden />
                <Text variant='b5' color='subtext'>
                  {city}, {state}
                </Text>
              </Flex>
            )}
          </Flex>
          <Flex direction='column' gap='sm'>
            {applicationCtaUrl && (
              <Button
                variant='primaryFill'
                size='lg'
                className='w-full !h-auto min-h-10'
                onClick={(e) => handleApplication(e, applicationCtaUrl)}
              >
                {applicationCtaLabel || 'Next Steps'}
              </Button>
            )}
            {extraCtaHref && (
              <Button
                variant='primaryFill'
                size='lg'
                className='w-full !h-auto min-h-10'
                onClick={handleExtraCta}
              >
                {extraCtaLabel}
              </Button>
            )}
          </Flex>
          <Button
            variant='textOnly'
            size='sm'
            className='mx-auto'
            iconAfter='chevrons-right'
            onClick={onCloseExperience}
          >
            Continue browsing schools
          </Button>
        </Flex>
      )}
    </Section>
  );
};

export default ExitStrategy;
